import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, AlertTriangle, TrendingUp } from 'lucide-react';

const Results = () => {
  const results = [
    {
      title: "Real user scan: Plaque detection",
      description: "AI accurately identifies plaque buildup with 90% precision",
      icon: CheckCircle,
      color: "text-red-500",
      bgColor: "bg-red-50",
      borderColor: "border-red-200"
    },
    {
      title: "Gingivitis severity highlighted by tooth",
      description: "Advanced detection algorithms assess gum health in real-time",
      icon: AlertTriangle,
      color: "text-orange-500",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200"
    },
    {
      title: "Tracked progression over time",
      description: "Monitor improvements and maintain optimal oral health",
      icon: TrendingUp,
      color: "text-green-500",
      bgColor: "bg-green-50",
      borderColor: "border-green-200"
    }
  ];

  return (
    <section id="ai-models" className="section-padding bg-gray-900 text-white">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold mb-4">
            Results & Visual Examples
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            See how our AI models detect and analyze dental conditions with clinical-grade accuracy.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {results.map((result, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`${result.bgColor} ${result.borderColor} border rounded-2xl p-6 text-gray-900`}
            >
              <div className={`w-12 h-12 ${result.color} mb-4 flex items-center justify-center bg-white rounded-xl`}>
                <result.icon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{result.title}</h3>
              <p className="text-gray-600">{result.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Dental Images Results */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8"
        >
          {/* Device in Use 1 */}
          <div className="bg-white rounded-2xl p-6 shadow-xl">
            <img 
              src="/images/dental/device-in-use-1.png" 
              alt="Oralytics Device in Clinical Use" 
              className="w-full h-48 object-cover rounded-xl mb-4"
            />
            <h4 className="font-semibold text-gray-900 mb-2">Clinical Application</h4>
            <p className="text-sm text-gray-600">Device being used in clinical setting</p>
          </div>

          {/* Device in Use 2 */}
          <div className="bg-white rounded-2xl p-6 shadow-xl">
            <img 
              src="/images/dental/device-in-use-2.png" 
              alt="Oralytics Device Demonstration" 
              className="w-full h-48 object-cover rounded-xl mb-4"
            />
            <h4 className="font-semibold text-gray-900 mb-2">Device Demonstration</h4>
            <p className="text-sm text-gray-600">Showing device functionality</p>
          </div>

          {/* Device in Use 3 */}
          <div className="bg-white rounded-2xl p-6 shadow-xl">
            <img 
              src="/images/dental/device-in-use-3.png" 
              alt="Oralytics Device Testing" 
              className="w-full h-48 object-cover rounded-xl mb-4"
            />
            <h4 className="font-semibold text-gray-900 mb-2">Device Testing</h4>
            <p className="text-sm text-gray-600">Real-world testing and validation</p>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-gray-300 mb-6">
            Ready to experience AI-powered dental health monitoring?
          </p>
          <button 
            onClick={() => window.open('https://play.google.com/store/apps/details?id=com.oralytics.app', '_blank')}
            className="btn-primary"
          >
            Download Oralytics App
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Results; 